import ReactGA from 'react-ga4';

import { User } from 'src/shared/types/user';

import { ANALYTICS_MEASUREMENT_ID, ENVIRONMENT } from '../../settings';
import { info } from '../logger';

// Add this switch for debugging because this can be very noisy.
const SILENCED_LOGGING = true;

function pathMapper(path: string) {
  switch (path) {
    case '/':
      path = 'app-home';
      break;
    default:
      if (path.includes('/products/')) {
        path = `products/:productId`;
      }
      break;
  }

  return path;
}
//@TODO add notstaffkey when finish testing
const testingUsers = ['notstaff', 'preview', 'fullfeatured'];

export function init({ userInfo }: { userInfo?: User | null }) {
  if (userInfo) {
    const { username, isStaff, impersonating } = userInfo;
    const enableAnalytics =
      !isStaff &&
      !impersonating &&
      username &&
      !testingUsers.includes(username);

    if (ANALYTICS_MEASUREMENT_ID && enableAnalytics) {
      //Assuming that we don't track anything if there's no user logged in.
      ReactGA.initialize(ANALYTICS_MEASUREMENT_ID);
      ReactGA.set({ user_id: username });

      return (
        !SILENCED_LOGGING &&
        info(
          `[Analytics] GA initialized with measurement ID [${ANALYTICS_MEASUREMENT_ID}]`
        )
      );
    }

    return info(
      `[Analytics] GA not initialized - isStaff: ${isStaff} - isImpersonating: ${JSON.stringify(impersonating)} - isTestingUser: ${testingUsers.includes(username)}`
    );
  }

  return info(`[Analytics] GA not initialized - no user info provided`);
}

/** Guards against calling ReactGA.initialize more than once in the same session. */
let anonymousGaInitialized = false;

/**
 * Initializes GA4 without a signed-in user. Returns false when the measurement
 * ID is unset so callers can no-op. Used on public conversion pages where
 * PrivateRoute (and therefore `init()`) never runs.
 */
function ensureAnonymousGaInitialized(): boolean {
  if (!ANALYTICS_MEASUREMENT_ID) return false;

  if (!anonymousGaInitialized) {
    ReactGA.initialize(ANALYTICS_MEASUREMENT_ID);
    anonymousGaInitialized = true;
  }

  return true;
}

/**
 * Initializes GA without a signed-in user (if not already done) and fires a
 * pageview. Used on public conversion pages where PrivateRoute never runs, so
 * the standard init/trackPageView path is unavailable.
 */
export function trackAnonymousPageView(path: string) {
  if (!ensureAnonymousGaInitialized()) return;

  trackPageView(path);
}

/**
 * Fires the GA4 recommended `sign_up` event.
 *
 * Should be called exactly once when the user lands on /register/thank-you.
 * Initializes anonymous GA first because PrivateRoute never mounts for a
 * just-registered visitor. No-ops outside production and when the
 * measurement ID is unset.
 */
export function trackSignupEvent(): void {
  if (ENVIRONMENT !== 'production') return;
  if (!ensureAnonymousGaInitialized()) return;

  ReactGA.event('sign_up', {
    method: 'email'
  });

  !SILENCED_LOGGING && info('[Analytics] sign_up event sent');
}

export function trackPageView(path: string) {
  if (ANALYTICS_MEASUREMENT_ID) {
    ReactGA.send({
      hitType: 'pageview',
      page: pathMapper(path),
      title: document.title
    });

    return (
      !SILENCED_LOGGING &&
      info(`[Analytics] page view sent: ${pathMapper(path)}`)
    );
  }
}
